import axios from "axios"
import { Box, Text, Link } from "@chakra-ui/react"
import oktaEmailRegex from "lib/oktaEmailRegex"

const professions = {
    clinician: [
        "Physician",
        "Fellow/Resident",
        "Technician/Technologist",
        "Physician Assistant",
        "Nurse/Nurse Practitioner",
        "Engineer/Scientist",
        "Pharmacist",
        "Data Manager/ Biostatisticians",
        "Administration",
        "Non Physician Researcher",
        "Medical Writer/Reporter",
        "Other",
    ],
    nonClinician: [
        "CEO/President/VP",
        "Sales/Marketing Representative",
        "Engineer/Scientist",
        "Physician (non-clinician)",
        "Government Official",
        "Hospital Administrator",
        "Media/Press",
        "General Public/Patient",
        "Other",
    ],
}

const specialties = {
    clinician: [
        "Interventional Cardiology",
        "Clinical Cardiology",
        "Vascular Surgery",
        "Cardiac Surgery",
        "Internal Medicine",
        "Interventional Radiology",
        "Neuroradiology",
        "Radiology",
        "Pediatric Cardiology",
        "Pediatrics",
        "Emergency Medicine",
        "Neurology",
        "Echocardiography",
        "Endocrinology",
        "Diabetolgy",
        "Vascular Medicine",
        "Research",
        "FDA/NIH/CMS",
    ],
    nonClinician: [
        "Medical Device",
        "Pharmaceutical",
        "Healthcare Services",
        "Financial Services",
        "Biotechnology",
        "Legal",
        "Other",
    ],
}

const createAccountFormConfig = {
    submitLabel: "Register",
    sections: [
        {
            title: "Contact Information",
            rows: [
                {
                    fields: [
                        {
                            name: "firstName",
                            label: "First Name",
                            type: "text",
                            rules: {
                                required: "First name is required",
                            },
                        },
                        {
                            name: "lastName",
                            label: "Last Name",
                            type: "text",
                            rules: {
                                required: "Last name is required",
                            },
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "email",
                            label: "Email",
                            type: "email",
                            rules: {
                                required: "Email is required",
                                custom: "uniqueEmail",
                                pattern: {
                                    value: oktaEmailRegex,
                                    message: "Please use a valid email address",
                                },
                            },
                            help: "This will also be your username",
                        },
                        {
                            name: "primaryPhone",
                            label: "Phone",
                            type: "text",
                            rules: {
                                required: "A phone number is required",
                            },
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "streetAddress",
                            label: "Street Address",
                            type: "text",
                            rules: {
                                required: "An address is required",
                            },
                        },
                        {
                            name: "city",
                            label: "City",
                            type: "text",
                            rules: {
                                required: "City is required",
                            },
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "country",
                            label: "Country",
                            type: "select",
                            options: "https://api.crf.org/countries",
                            adapter: data => {
                                return data.data.map(c => {
                                    return {
                                        label: c.name,
                                        value: `${c.code}:${c.name}`,
                                    }
                                })
                            },
                            rules: {
                                required: "Country is required",
                            },
                        },
                        {
                            name: "zipCode",
                            label: "Postal / Zip Code",
                            type: "text",
                            rules: {
                                required: "Zipcode is required",
                            },
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "state",
                            label: "State",
                            type: "select_dependent",
                            options: {
                                depends_on: "country",
                                generate: async country => {
                                    let _opts = []
                                    const countryCode = country.split(":")[0]
                                    await axios
                                        .get(`https://api.crf.org/countries/${countryCode}/states`)
                                        .then(response => {
                                            _opts = response.data.data.map(st => {
                                                return {
                                                    label: st.name,
                                                    value: st.name,
                                                }
                                            })
                                        })
                                        .catch(error => {
                                            return false
                                        })
                                    return _opts
                                },
                            },
                            rules: {
                                required: "State is required",
                            },
                        },
                        {
                            type: "spacer",
                            name: "spacer1",
                        },
                    ],
                },
            ],
        },
        {
            title: "Account Information",
            rows: [
                {
                    fields: [
                        {
                            name: "password",
                            label: "Password",
                            type: "password",
                            rules: {
                                required: "Password is required",
                                minLength: {
                                    value: 4,
                                    message: "Must be at least 4 characters",
                                },
                            },
                        },
                        {
                            name: "password-confirm",
                            label: "Confirm Password",
                            type: "password",
                            rules: {
                                required: "Please confirm the password",
                                equal: {
                                    field: "password",
                                    message: "Passwords must match",
                                },
                            },
                        },
                    ],
                },
            ],
        },
        {
            title: "Professional Information",
            rows: [
                {
                    fields: [
                        {
                            name: "organization",
                            label: "Company / Institution",
                            type: "autocomplete",
                            options: "/api/emma/companies?search=[[input]]",
                            adapter: data => {
                                // console.log(data)
                                return data.results.map(company => {
                                    return {
                                        label: company,
                                        value: company,
                                    }
                                })
                            },
                            rules: {
                                required: "Company / Institution is required",
                            },
                            help: "Your primary institution",
                            params: {
                                creatable: true,
                            },
                        },
                        {
                            name: "title",
                            label: "Title",
                            type: "text",
                            rules: {
                                required: "Title is required",
                            },
                            help: "At your primary institution",
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "is_clinician",
                            label: "Are you a Clinician or Scientist?",
                            type: "radio",
                            rules: {
                                required: "This is a required field",
                            },
                            options: [
                                {
                                    label: "Yes",
                                    value: "true",
                                },
                                {
                                    label: "No",
                                    value: "false",
                                },
                            ],
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "profession",
                            label: "Profession",
                            type: "select_dependent",
                            options: {
                                depends_on: "is_clinician",
                                // showEmpty: true,
                                generate: async clinician => {
                                    const options = professions[clinician === "true" ? "clinician" : "nonClinician"]
                                    return options.map(opt => {
                                        return {
                                            label: opt,
                                            value: opt,
                                        }
                                    })
                                },
                            },
                            rules: {
                                required: "Profession is required",
                            },
                        },
                        {
                            name: "specialty",
                            label: "Specialty",
                            type: "select_dependent",
                            options: {
                                depends_on: "is_clinician",
                                // showEmpty: true,
                                generate: async clinician => {
                                    const options = specialties[clinician === "true" ? "clinician" : "nonClinician"]
                                    return options.map(opt => {
                                        return {
                                            label: opt,
                                            value: opt,
                                        }
                                    })
                                },
                            },
                            rules: {
                                required: "Specialty is required",
                            },
                        },
                    ],
                },
            ],
        },
        {
            title: "Newsletter",
            rows: [
                {
                    fields: [
                        {
                            type: "readonly",
                            name: "news-info",
                            value: (
                                <Box>
                                    <Text mt={1} mb={1}>
                                        Newsletter subscribers will receive occasional messages from our supporters and
                                        partners. We will never share your information with third parties.
                                    </Text>
                                    <Text mt={1} mb={1}>
                                        Also, you may easily update your newsletter preferences or unsubscribe at any
                                        time at the bottom of the email/newsletter you receive
                                    </Text>
                                    <Text mt={1}>
                                        Registration confirms that you agree to the{" "}
                                        <Link
                                            href="https://www.crf.org/privacy-policy"
                                            textDecoration="underline"
                                            target="_blank"
                                        >
                                            CRF Privacy Policy
                                        </Link>
                                        .
                                    </Text>
                                </Box>
                            ),
                        },
                    ],
                },
                {
                    fields: [
                        {
                            name: "tctmd_newsletter",
                            type: "checkbox",
                            label: (
                                <CheckboxLabel
                                    title="TCTMD NEWS"
                                    body="Provides the latest news, research, and major meeting presentations"
                                />
                            ),
                        },
                        {
                            name: "crf_newsletter",
                            type: "checkbox",
                            label: (
                                <CheckboxLabel
                                    title="CRF NEWS & ANNOUNCEMENTS"
                                    body="Provides important news and updates from CRF and all of our major meetings
            including TCT, CTO, TVT, Fellows, and other CRF events"
                                />
                            ),
                        },
                    ],
                },
            ],
        },
    ],
}

function CheckboxLabel({ title, body }) {
    return (
        <Text transform={"translateY(50%)"} mt="-5%">
            <Text as="span" fontWeight={600}>
                {title}
            </Text>{" "}
            <br /> {body}
        </Text>
    )
}

export default createAccountFormConfig
